import { scopedThreadKey } from "@afar-research/client-runtime/environment";
import type { OrchestrationProposedPlanId, ScopedThreadRef } from "@afar-research/contracts";
import { create } from "zustand";

interface PendingProposedPlanHandoff {
  readonly sourcePlanId: OrchestrationProposedPlanId;
}

export interface ProposedPlanHandoffState {
  readonly bySourceThreadKey: Readonly<Record<string, PendingProposedPlanHandoff>>;
}

interface ProposedPlanHandoffStore extends ProposedPlanHandoffState {
  readonly begin: (input: {
    readonly sourceThreadRef: ScopedThreadRef;
    readonly sourcePlanId: OrchestrationProposedPlanId;
  }) => void;
  readonly clear: (sourceThreadRef: ScopedThreadRef) => void;
  readonly clearAll: () => void;
  readonly reconcile: (actionableSourceThreadRefs: ReadonlyArray<ScopedThreadRef>) => void;
}

const EMPTY_HANDOFFS: Readonly<Record<string, PendingProposedPlanHandoff>> = {};

export function isProposedPlanHandoffPending(
  state: ProposedPlanHandoffState,
  input: {
    readonly sourceThreadRef: ScopedThreadRef;
    readonly sourcePlanId: OrchestrationProposedPlanId;
  },
): boolean {
  return (
    state.bySourceThreadKey[scopedThreadKey(input.sourceThreadRef)]?.sourcePlanId ===
    input.sourcePlanId
  );
}

export function hasProposedPlanHandoffForThread(
  state: ProposedPlanHandoffState,
  sourceThreadRef: ScopedThreadRef,
): boolean {
  return state.bySourceThreadKey[scopedThreadKey(sourceThreadRef)] !== undefined;
}

export const useProposedPlanHandoffStore = create<ProposedPlanHandoffStore>()((set) => ({
  bySourceThreadKey: EMPTY_HANDOFFS,
  begin: ({ sourceThreadRef, sourcePlanId }) =>
    set((state) => ({
      bySourceThreadKey: {
        ...state.bySourceThreadKey,
        [scopedThreadKey(sourceThreadRef)]: { sourcePlanId },
      },
    })),
  clear: (sourceThreadRef) =>
    set((state) => {
      const sourceThreadKey = scopedThreadKey(sourceThreadRef);
      if (!(sourceThreadKey in state.bySourceThreadKey)) {
        return state;
      }
      const next = { ...state.bySourceThreadKey };
      delete next[sourceThreadKey];
      return { bySourceThreadKey: next };
    }),
  clearAll: () =>
    set((state) =>
      Object.keys(state.bySourceThreadKey).length === 0
        ? state
        : { bySourceThreadKey: EMPTY_HANDOFFS },
    ),
  reconcile: (actionableSourceThreadRefs) =>
    set((state) => {
      const actionableSourceThreadKeys = new Set(actionableSourceThreadRefs.map(scopedThreadKey));
      const next = Object.fromEntries(
        Object.entries(state.bySourceThreadKey).filter(([sourceThreadKey]) =>
          actionableSourceThreadKeys.has(sourceThreadKey),
        ),
      );
      if (Object.keys(next).length === Object.keys(state.bySourceThreadKey).length) {
        return state;
      }
      return { bySourceThreadKey: next };
    }),
}));
